import type { PaymentCurrency } from './payments';
import type { IncidentDataSnapshot, IncidentStatus } from './incidents';

export type CustomerBrand = {
  id: string;
  brandCode: string;
  brandSlug: string | null;
  brandName: string;
  merchantName: string | null;
  brandApiUrl: string | null;
};

export type CustomerSubscription = {
  id: string;
  customerId: string;
  currencyId: string;
  paidTill: string | null;
  canceledAt: string | null;
  canceledBy: string | null;
  isPaused: boolean | null;
  pausedTill: string | null;
  externalId: string | null;
  createdAt: string;
  updatedAt: string;
  paymentDescriptor?: string | null;
  features?: string[] | null;
  offerType?: string | null;
  /** Original start date of a migrated subscription; createdAt is the migration moment. */
  startedAt?: string | null;
};

export const CustomerPaymentMethodStatuses = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

export type CustomerPaymentMethodStatus =
  (typeof CustomerPaymentMethodStatuses)[keyof typeof CustomerPaymentMethodStatuses];

export type DashboardCustomerPaymentMethod = {
  id: string;
  customerId: string;
  status: string;
  provider: string;
  providerCustomerId: string | null;
  type: string | null;
  network: string | null;
  firstName: string | null;
  lastName: string | null;
  firstDigits: string | null;
  lastDigits: string | null;
  issuerCountry: string | null;
  issuerName: string | null;
  expiresAt: string | null;
  legalCompany: string | null;
  externalOrderId: string | null;
  externalOrderToken: string | null;
  preventRecurringPaymentsTill: string | null;
  createdAt: string;
  updatedAt: string;
};

/** An incident the customer is attached to, with the attachment's own note/source/time. */
export type CustomerIncident = {
  id: string;
  name: string;
  status: IncidentStatus;
  note: string | null;
  source: string | null;
  previousData: IncidentDataSnapshot | null;
  newData: IncidentDataSnapshot | null;
  attachedAt: string;
};

export type CustomerDebtCollection = {
  id: string;
  customerId: string;
  subscriptionId: string;
  subscriptionCreatedAt: string;
  currencyId: string;
  status: string;
  amount: number;
  amountInEuro: number;
  collectedAmount: number;
  cycleCount: number;
  succeededCount: number;
  collectedAt: string | null;
  /** Next charge attempt (execute_at of the upcoming scheduled payment), when one is scheduled. */
  upcomingExecuteAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CustomerPaymentSession = {
  id: string;
  provider: string;
  amount: number;
  amountInEuro: number;
  paymentDescriptor: string | null;
  purchaseItemType: string | null;
  externalOrderId: string | null;
  externalOrderToken: string | null;
  originUrl: string | null;
  currencyId: string;
  brandId: string;
  offerId: string | null;
  offerPriceId: string | null;
  purchaseItemId: string | null;
  customerId: string;
  priceProductObjectId: string | null;
  priceProductObjectPriceId: string | null;
  externalOfferId: string | null;
  subscriptionId: string | null;
  subscriptionCreatedAt: string | null;
  unsubscribeSubscriptionId: string | null;
  providerId: string | null;
  subscriptionCycle: number | null;
  debtCollectionId: string | null;
  subscriptionRecoveryId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CustomerPayment = {
  id: string;
  amount: number;
  amountInEuro: number;
  amountRefunded: number;
  currencyId: string;
  provider: string;
  providerId: string | null;
  status: string;
  providerStatus: string | null;
  processor: string | null;
  collectionType: string | null;
  paymentSessionId: string;
  paymentSessionCreatedAt: string;
  customerPaymentMethodId: string | null;
  providerDeclineType: string | null;
  providerDeclineCode: string | null;
  providerDeclineMessage: string | null;
  authorizationCode: string | null;
  capturedAt: string | null;
  lastCaptureAttemptAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** One scheduled (recurring/offset) charge attempt. `amount` (native charge) and `currency` are enriched
 * together from the same source (executed session → price snapshot → initial session), so they always
 * agree; `amountInEuro` is the euro-normalized equivalent. */
export type CustomerScheduledPayment = {
  id: string;
  subscriptionId: string | null;
  subscriptionCreatedAt: string | null;
  nnpOrderId: string | null;
  executeAt: string;
  status: string;
  retryTime: number;
  chargerType: string | null;
  chargerVersion: number | null;
  previousErrorCode: string | null;
  systemError: string | null;
  debtCollectionId: string | null;
  subscriptionRecoveryId: string | null;
  amount: number | null;
  /** Amount of the payment this row produced once it executed; null when no payment exists (e.g. a row
   * that failed before any charge). Distinguishes a real charge from a not-yet/never-charged row. */
  chargedAmount: number | null;
  amountInEuro: number | null;
  paymentDescriptor: string | null;
  offerType: string | null;
  features: string[] | null;
  /** Provider (PSP) id of the payment this row produced once it executed; null while still upcoming. */
  providerId: string | null;
  currency: PaymentCurrency | null;
  createdAt: string;
  updatedAt: string;
};

export type CustomerRefund = {
  id: string;
  numericId: number;
  amount: number;
  currencyId: string;
  status: string;
  reasons: string[] | null;
  completedAt: string | null;
  paymentId: string;
  paymentCreatedAt: string;
  /** Provider (e.g. Primer) id of the refunded payment. */
  paymentProviderId: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Normalized payload stored on a dispute event; richer on DISPUTE.STATUS than on DISPUTE.OPENED. */
export type CustomerDisputeRequest = {
  eventType?: string;
  processorDisputeId?: string;
  /** 'DISPUTE' | 'RETRIEVAL' | 'FRAUD_WARNING' */
  type?: string;
  status?: string;
  reason?: string;
  reasonCode?: string;
  processorReason?: string;
  processor?: string;
  /** DISPUTE.OPENED events carry the processor under this key instead. */
  processorId?: string;
  orderId?: string;
  receivedAt?: string;
};

/** Dispute event row (one case can have several: DISPUTE.OPENED, then DISPUTE.STATUS updates). */
export type CustomerDispute = {
  id: string;
  eventType: string;
  status: string;
  /** Short processor payment id, e.g. '5VnDuLXMu'. */
  providerId: string | null;
  transactionId: string | null;
  /** Minor units in `currency` (not euro). */
  amount: number;
  currency: string;
  rawProcessorCallback: unknown;
  request: CustomerDisputeRequest | null;
  paymentId: string | null;
  paymentCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Chargeback alert row (Chargebacks911 Ethoca/RDR/Order Insight alerts, Stripe Radar warnings). */
export type CustomerChargebackAlert = {
  id: string;
  status: string;
  provider: string;
  externalId: string | null;
  /** Raw provider payload; shape differs per provider (cb911 alert vs Stripe Radar event). */
  externalData: unknown;
  /** Minor units in `paymentCurrency` (not euro). */
  paymentAmount: number;
  paymentDescriptor: string | null;
  paymentCurrency: string;
  paymentDate: string | null;
  cardLastDigits: string | null;
  cardNumber: string | null;
  cardIssuer: string | null;
  receivedAt: string | null;
  syncedAt: string | null;
  completedBy: string | null;
  reasonCompleted: string | null;
  paymentId: string | null;
  paymentCreatedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CustomerSubscriptionRecovery = {
  id: string;
  subscriptionId: string;
  subscriptionCreatedAt: string;
  originalChargeAmount: number;
  totalRecoveredAmount: number;
  recoveredAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type DashboardCustomer = {
  id: string;
  numericId: string;
  primerCustomerId: string | null;
  app: string;
  email: string;
  externalId: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  geography: string | null;
  originGeoVatCountry: string | null;
  country: string | null;
  originOrderPaymentAt: string | null;
  originOrderPaymentIPAddress: string | null;
  originOrderPaymentIPCountry: string | null;
  originOrderPaymentMethodCountry: string | null;
  testGroup: number | null;
  locale: string | null;
  gender: string | null;
  age: number | null;
  heightCm: number | null;
  weightKg: number | null;
  targetWeightKg: number | null;
  measurementSystem: string | null;
  marketingAgreed: boolean;
  testUser: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  metadata: Record<string, unknown> | null;
  brand: CustomerBrand | null;
  // Every currency the customer's rows reference. Only scheduledPayments embed a full currency object;
  // subscriptions, payments, refunds and debts carry a bare currencyId resolved against this list.
  currencies: PaymentCurrency[];
  subscriptions: CustomerSubscription[];
  customerPaymentMethods: DashboardCustomerPaymentMethod[];
  debtCollections: CustomerDebtCollection[];
  paymentSessions: CustomerPaymentSession[];
  payments: CustomerPayment[];
  scheduledPayments: CustomerScheduledPayment[];
  refunds: CustomerRefund[];
  disputes: CustomerDispute[];
  chargebackAlerts: CustomerChargebackAlert[];
  subscriptionRecoveries: CustomerSubscriptionRecovery[];
  incidents: CustomerIncident[];
};
